/**
 * AnalyserNode wrapper that produces a stable AudioFrame each frame.
 *
 * Phase 0 keeps everything on the main thread per the plan; an AudioWorklet
 * is deferred to Phase 3 unless latency becomes a real problem.
 */

import { BeatDetector } from './beatDetector';
import { extractBands } from './bands';

export type AudioFrame = {
  time: number;
  dt: number;
  rms: number;
  bass: number;
  mid: number;
  treble: number;
  spectrum: Float32Array; // 256 log-binned bands, 0..1
  waveform: Float32Array; // 1024 samples, -1..1
  beat: {
    confidence: number;
    tick: boolean;
    flux: number;
    threshold: number;
    bpm: number | null;
    msSinceLastBeat: number | null;
  };
  history: { bass1s: number; rms1s: number };
};

const FFT_SIZE = 2048;
const SPECTRUM_BINS = 256;
const WAVEFORM_LEN = 1024;
const SMOOTHING_BASS = 0.78;
const SMOOTHING_MID = 0.7;
const SMOOTHING_TREBLE = 0.6;
const SMOOTHING_RMS = 0.7;
const HISTORY_DECAY_PER_S = 0.5;

export type AudioPipeline = {
  ctx: AudioContext;
  analyser: AnalyserNode;
  /** Source-side connection point. Connect a MediaStreamSource / BufferSource here. */
  input: GainNode;
  read: (now: number) => AudioFrame;
};

export function createAudioPipeline(): AudioPipeline {
  const Ctx =
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ??
    AudioContext;
  const ctx = new Ctx({ latencyHint: 'interactive' });

  const input = ctx.createGain();
  input.gain.value = 1.0;

  const analyser = ctx.createAnalyser();
  analyser.fftSize = FFT_SIZE;
  analyser.smoothingTimeConstant = 0.5;
  analyser.minDecibels = -90;
  analyser.maxDecibels = -10;

  input.connect(analyser);
  // We deliberately do NOT connect analyser -> ctx.destination to avoid
  // double-monitoring (the user already hears the source through their tab
  // capture / mic / OS output). The mic path may want sidechain monitoring,
  // but the demo clip handles its own destination.

  const fdData = new Uint8Array(analyser.frequencyBinCount);
  const tdData = new Float32Array(analyser.fftSize);
  const spectrumOut = new Float32Array(SPECTRUM_BINS);
  const waveformOut = new Float32Array(WAVEFORM_LEN);

  const beat = new BeatDetector();
  const state = {
    rms: 0,
    bass: 0,
    mid: 0,
    treble: 0,
    bass1s: 0,
    rms1s: 0,
    lastTime: 0,
  };

  function read(now: number): AudioFrame {
    const time = now;
    const dt = state.lastTime > 0 ? Math.max(0, time - state.lastTime) : 1 / 60;
    state.lastTime = time;

    analyser.getByteFrequencyData(fdData);
    analyser.getFloatTimeDomainData(tdData);

    // Log-binning frequency data into SPECTRUM_BINS buckets, normalized 0..1.
    logBin(fdData, spectrumOut);
    // Downsample / copy waveform into a fixed-size buffer.
    downsampleWaveform(tdData, waveformOut);

    // Energy bands (Hz-based cutoffs via bands.ts so they don't drift with
    // FFT size or sample rate).
    const { bass: bassRaw, mid: midRaw, treble: trebRaw } = extractBands(
      fdData,
      ctx.sampleRate,
    );

    let rmsRaw = 0;
    for (let i = 0; i < tdData.length; i++) {
      const v = tdData[i] ?? 0;
      rmsRaw += v * v;
    }
    rmsRaw = Math.sqrt(rmsRaw / tdData.length);

    state.bass = lerp(state.bass, bassRaw, 1 - Math.pow(SMOOTHING_BASS, 60 * dt));
    state.mid = lerp(state.mid, midRaw, 1 - Math.pow(SMOOTHING_MID, 60 * dt));
    state.treble = lerp(state.treble, trebRaw, 1 - Math.pow(SMOOTHING_TREBLE, 60 * dt));
    state.rms = lerp(state.rms, rmsRaw, 1 - Math.pow(SMOOTHING_RMS, 60 * dt));

    // 1s decaying max.
    const decay = Math.pow(HISTORY_DECAY_PER_S, dt);
    state.bass1s = Math.max(state.bass, state.bass1s * decay);
    state.rms1s = Math.max(state.rms, state.rms1s * decay);

    const beatResult = beat.update(spectrumOut, time);

    return {
      time,
      dt,
      rms: state.rms,
      bass: state.bass,
      mid: state.mid,
      treble: state.treble,
      spectrum: spectrumOut,
      waveform: waveformOut,
      beat: beatResult,
      history: { bass1s: state.bass1s, rms1s: state.rms1s },
    };
  }

  return { ctx, analyser, input, read };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.min(1, Math.max(0, t));
}

function downsampleWaveform(src: Float32Array, dst: Float32Array): void {
  const stride = src.length / dst.length;
  for (let i = 0; i < dst.length; i++) {
    const idx = Math.floor(i * stride);
    dst[i] = src[idx] ?? 0;
  }
}

function logBin(src: Uint8Array, dst: Float32Array): void {
  // Log-spaced bin edges across the input spectrum.
  const n = src.length;
  const m = dst.length;
  const minIdx = 1;
  const lnMin = Math.log(minIdx);
  const lnMax = Math.log(n);
  for (let i = 0; i < m; i++) {
    const t0 = i / m;
    const t1 = (i + 1) / m;
    const a = Math.floor(Math.exp(lnMin + (lnMax - lnMin) * t0));
    const b = Math.max(a + 1, Math.floor(Math.exp(lnMin + (lnMax - lnMin) * t1)));
    let sum = 0;
    let count = 0;
    for (let j = a; j < b && j < n; j++) {
      sum += src[j] ?? 0;
      count++;
    }
    dst[i] = count > 0 ? sum / count / 255 : 0;
  }
}
